import React from 'react';
import { makeStyles } from 'tss-react/mui';
import MetronomePlayer from './metronome-player';

const useStyles = makeStyles()((theme) => ({
  metronome: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  beatIndicator: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  beat: {
    width: '1rem',
    height: '1rem',
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[500],
  },
  activeBeat: {
    backgroundColor: theme.palette.grey[700],
  },
  firstActiveBeat: {
    backgroundColor: theme.palette.primary.main,
  },
}));

export interface MetronomeProps {
  bpm: number;
  timeSig: number;
  isOn: boolean;
  onTick?: (currentTick: number, offset: number) => void;
}

const Metronome: React.FC<MetronomeProps> = ({ bpm, timeSig, isOn, onTick }) => {
  const { classes } = useStyles();

  const [currentBeat, setCurrentBeat] = React.useState(0);
  const playerRef = React.useRef<MetronomePlayer | null>(null);

  const handleBeatChange = React.useCallback(
    (beat: number, offset: number) => {
      setCurrentBeat(beat);
      if (onTick) {
        onTick(beat, offset);
      }
    },
    [onTick],
  );

  React.useEffect(() => {
    if (!playerRef.current) {
      playerRef.current = new MetronomePlayer(bpm, timeSig, handleBeatChange);
    } else {
      playerRef.current.updateBpm(bpm);
      playerRef.current.updateTimeSig(timeSig);
      playerRef.current.setOnBeatChangeCallback(handleBeatChange);
    }
  }, [bpm, timeSig, handleBeatChange]);

  React.useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (playerRef.current) {
      if (isOn) {
        playerRef.current.start();
      } else {
        playerRef.current.stop();
      }
    }
  }, [isOn]);

  return (
    <div className={classes.metronome}>
      <div className={classes.beatIndicator}>
        {Array.from({ length: timeSig }).map((_, i) => {
          let activeClass = '';
          if (currentBeat === i) {
            if (i === 0) {
              activeClass = classes.firstActiveBeat;
            } else {
              activeClass = classes.activeBeat;
            }
          }

          // eslint-disable-next-line react/no-array-index-key
          return <div key={`beat-${i}`} className={`${classes.beat} ${activeClass}`} />;
        })}
      </div>
    </div>
  );
};

export default Metronome;
